import { createFileRoute, Link } from '@tanstack/react-router';
import { basicHead } from '../../../utility/head';
import { getButtonStyles } from '@sc/shared-ui';
import { useCaseData } from '../../../assets/data/useCases';
import Breadcrumbs from '../../../components/Breadcrumbs';
import EmpowerShape from '../../../assets/shapes/empower.svg?react';
import { BottomCTA } from '../../../components/BottomCTA';
import { ResponsiveImage } from '../../../components/ResponsiveImage';

export const Route = createFileRoute('/_marketing/use-cases/')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Market research crawl use cases | Stark Crawl',
      description:
        'Use Stark Crawl for directory coverage, prospect capture, competitor monitoring, and structured data extraction across markets.',
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <div className="relative overflow-x-hidden">
      <EmpowerShape
        className="absolute right-0 top-20 translate-x-1/2 w-216 opacity-4 pointer-events-none"
        aria-hidden="true"
      />

      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-10 px-5">
        <div className="max-w-315 mx-auto text-center">
          <Breadcrumbs
            center
            items={[{ label: 'Home', path: '/' }, { label: "What it's for" }]}
          />
          <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white mt-5">
            Market research <span className="text-primary-400">use cases</span>
          </h1>
          <p className="text-lg leading-6.5 text-grey-300 max-w-180 mx-auto mt-5">
            Directories, competitor sites, and vertical listings become structured
            prospect data — discover hosts, crawl pages, extract fields, and
            capture what your research team needs.
          </p>
        </div>
      </section>

      {/* ─── Use case cards ──────────────────────────────────────────────── */}
      <section className="pb-25 px-5">
        <div
          className="max-w-315 mx-auto grid grid-cols-1 md:grid-cols-2 gap-5"
          role="region"
          aria-label="What it's for"
        >
          {useCaseData.useCases.map((useCase) => (
            <Link
              key={useCase.path}
              to="/use-cases/$slug"
              params={{ slug: useCase.path }}
              className="flex flex-col bg-background-tertiary rounded-xs overflow-hidden group backdrop-blur-[6.25rem]"
            >
              <ResponsiveImage
                className="w-full aspect-video object-cover"
                src={useCase.cardImage}
                alt={useCase.headline}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
              />
              <div className="flex flex-col p-5 flex-1">
                <h3 className="font-grotesque text-[1.75rem] mb-4 leading-8 font-semibold text-white">
                  {useCase.headline}
                </h3>
                <p className="text-base leading-6 mb-8 text-grey-200 flex-1 line-clamp-4">
                  {useCase.blurb}
                </p>
                <div
                  {...getButtonStyles({
                    type: 'low-priority',
                    size: 'large',
                    additionalClassnames: 'w-fit',
                  })}
                >
                  <span>{useCase.headTerm}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ─── Bottom CTA ──────────────────────────────────────────────────── */}
      <BottomCTA secondaryTo="/pricing" secondaryLabel="View pricing" />
    </div>
  );
}
